import { useState, useCallback, useEffect } from "react";
import { AuthScreen } from "@/components/screens/AuthScreen";
import { GroupSelectScreen } from "@/components/screens/GroupSelectScreen";
import { HomeScreen } from "@/components/screens/HomeScreen";
import { TallyScreen } from "@/components/screens/TallyScreen";
import { ScoreboardScreen } from "@/components/screens/ScoreboardScreen";
import { SettingsScreen, type SettingsPage } from "@/components/screens/SettingsScreen";
import { HalkaScreen } from "@/components/screens/HalkaScreen";
import { MessagesScreen } from "@/components/screens/MessagesScreen";
import { HatimScreen } from "@/components/screens/HatimScreen";
import { FriendsScreen } from "@/components/screens/FriendsScreen";
import {
  ChangePasswordPage, AppearancePage, NotificationSettingsPage,
  LanguagePage, TermsPage, PrivacyPage, HelpPage, FeedbackPage,
  ProfileEditPage,
} from "@/components/screens/SettingsPages";
import { QuizModal } from "@/components/screens/QuizModal";
import { BottomNav, type Tab } from "@/components/layout/BottomNav";
import { api, setToken } from "@/api/client";
import type { Locale } from "@/lib/i18n";

// ---------------------------------------------------------
//  Types
// ---------------------------------------------------------

type Screen = "auth" | "group-select" | "main";

interface UserGroup {
  groupId: string;
  groupName: string;
  role: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  totalPoints: number;
  streak: number;
  sadakaDebt: number;
  emailVerified: boolean;
  groups: UserGroup[];
}

interface Goal {
  id: string; title: string; emoji: string; points: number;
  mandatory: boolean; category: string; sadakaCost: number; completed: boolean;
}

interface Quote {
  text_tr: string; text_ar?: string; source: string; source_detail?: string;
}

interface AvailableGroup {
  id: string; name: string; description?: string; memberCount: number; maxMembers: number;
}

interface MessageChannel {
  channelType: string;
  channelId: string;
  name: string;
}

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  createdAt: string;
  isMe: boolean;
}

interface Friend {
  id: string;
  name: string;
  email: string;
  status: "accepted" | "pending";
}

interface FriendRequest {
  id: string;
  fromUserId: string;
  fromUserName: string;
  fromUserEmail: string;
  createdAt: string;
}

// ---------------------------------------------------------
//  App
// ---------------------------------------------------------

export default function App() {
  // -- Core state --
  const [screen, setScreen] = useState<Screen>("auth");
  const [activeTab, setActiveTab] = useState<Tab>("home");
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // -- Active group for per-halka views --
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

  // -- Data state --
  const [goals, setGoals] = useState<Goal[]>([]);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [availableGroups, setAvailableGroups] = useState<AvailableGroup[]>([]);
  const [scoreboard, setScoreboard] = useState<Array<{
    userId: string; name: string; points: number; streak: number;
    todayPct: number; isMe: boolean; rank: number;
  }>>([]);
  const [weeklyData, setWeeklyData] = useState<Array<{ date: string; completed: number; total: number; points: number }>>([]);
  const [goalTallies, setGoalTallies] = useState<Array<{ goalId: string; title: string; shortTitle: string; emoji: string; mandatory: boolean; days: Record<string, boolean | null> }>>([]);
  const [monthlyData, setMonthlyData] = useState<Array<{ date: string; completed: number; total: number; points: number }>>([]);
  const [quiz, setQuiz] = useState<{ id: string; questions: Array<{ id: string; text: string; options: string[]; points: number }> } | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [scoreboardPeriod, setScoreboardPeriod] = useState("weekly");
  const [settingsPage, setSettingsPage] = useState<SettingsPage>("main");
  const [, setLocaleState] = useState(0);

  // -- Messages state --
  const [messagesChannels, setMessagesChannels] = useState<MessageChannel[]>([]);
  const [messagesList, setMessagesList] = useState<ChatMessage[]>([]);
  const [activeChannel, setActiveChannel] = useState<{ channelType: string; channelId: string } | null>(null);

  // -- Friends state --
  const [friends, setFriends] = useState<Friend[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);

  // -- Hatim state --
  const [showHatim, setShowHatim] = useState(false);
  const [hatimGroupId, setHatimGroupId] = useState<string | null>(null);

  // -- Derived: resolve active group for scoreboard etc. --
  const resolvedGroupId = activeGroupId ?? (user?.groups[0]?.groupId ?? null);


  // ========================================================
  //  Android back button
  // ========================================================
  useEffect(() => {
    const handleBackButton = (e: PopStateEvent) => {
      e.preventDefault();
      if (showQuiz) { setShowQuiz(false); return; }
      if (showHatim) { setShowHatim(false); return; }
      if (activeTab === "settings" && settingsPage !== "main") { setSettingsPage("main"); return; }
      if (activeChannel) { setActiveChannel(null); return; }
      if (activeTab !== "home") { setActiveTab("home"); return; }
      window.history.pushState(null, "", window.location.href);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBackButton);
    return () => window.removeEventListener("popstate", handleBackButton);
  }, [showQuiz, showHatim, activeTab, settingsPage, activeChannel]);

  // ========================================================
  //  Session check on mount
  // ========================================================
  useEffect(() => {
    const token = localStorage.getItem("ehille_token");
    if (token) {
      api<{ user: User }>("/auth/me")
        .then(data => {
          setUser(data.user);
          // If user has at least one group, go to main; otherwise group-select
          setScreen(data.user.groups.length > 0 ? "main" : "group-select");
          // Set first group as default active
          if (data.user.groups.length > 0) {
            setActiveGroupId(data.user.groups[0].groupId);
          }
        })
        .catch(() => { setToken(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // ========================================================
  //  Fetch daily quote (public)
  // ========================================================
  useEffect(() => {
    api<{ quote: Quote }>("/quotes/daily")
      .then(data => setQuote(data.quote))
      .catch(() => {});
  }, []);

  // ========================================================
  //  Fetch data when entering main screen
  // ========================================================
  useEffect(() => {
    if (screen !== "main" || !user) return;

    // Goals (backend filters by all user groups)
    api<{ goals: Goal[] }>("/goals").then(data => setGoals(data.goals)).catch(() => {});

    // Quiz
    api<{ quiz: typeof quiz }>("/quiz/current").then(data => setQuiz(data.quiz)).catch(() => {});

    // History
    api<{ history: Array<{ date: string; completed: number; total: number; points: number }> }>("/goals/history?period=weekly")
      .then(data => setWeeklyData(data.history)).catch(() => {});
    api<{ history: Array<{ date: string; completed: number; total: number; points: number }> }>("/goals/history?period=monthly")
      .then(data => setMonthlyData(data.history)).catch(() => {});

    // Tally grid data
    api<{ tallies: typeof goalTallies }>("/goals/tally")
      .then(data => setGoalTallies(data.tallies)).catch(() => {});

    // Auto-refresh goals every 60 seconds to keep colors/status current
    const refreshInterval = setInterval(() => {
      api<{ goals: Goal[] }>("/goals").then(data => setGoals(data.goals)).catch(() => {});
    }, 60_000);
    return () => clearInterval(refreshInterval);
  }, [screen, user]);

  // ========================================================
  //  Fetch scoreboard on tab change (with groupId param)
  // ========================================================
  useEffect(() => {
    if (activeTab !== "scoreboard" || !user || !resolvedGroupId) return;
    api<{ scoreboard: typeof scoreboard }>(`/scoreboard?period=${scoreboardPeriod}&groupId=${resolvedGroupId}`)
      .then(data => setScoreboard(data.scoreboard))
      .catch(() => {});
  }, [activeTab, scoreboardPeriod, user, resolvedGroupId]);

  // ========================================================
  //  Fetch available groups (always — user can be in multiple)
  // ========================================================
  useEffect(() => {
    if (!user) return;
    if (screen === "group-select" || activeTab === "halka") {
      api<{ groups: AvailableGroup[] }>("/groups")
        .then(data => setAvailableGroups(data.groups))
        .catch(() => {});
    }
  }, [screen, activeTab, user]);

  // ========================================================
  //  Fetch messages channels on tab change
  // ========================================================
  useEffect(() => {
    if (activeTab !== "messages" || !user) return;
    api<{ channels: MessageChannel[] }>("/messages/channels")
      .then(data => setMessagesChannels(data.channels))
      .catch(() => {});

    // Also fetch friends list for the messages screen
    api<{ friends: Friend[] }>("/friends")
      .then(data => setFriends(data.friends))
      .catch(() => {});
    api<{ requests: FriendRequest[] }>("/friends/requests")
      .then(data => setFriendRequests(data.requests))
      .catch(() => {});
  }, [activeTab, user]);

  // ========================================================
  //  Fetch friends when navigating to friends settings page
  // ========================================================
  useEffect(() => {
    if (settingsPage !== "friends" || !user) return;
    api<{ friends: Friend[] }>("/friends")
      .then(data => setFriends(data.friends))
      .catch(() => {});
    api<{ requests: FriendRequest[] }>("/friends/requests")
      .then(data => setFriendRequests(data.requests))
      .catch(() => {});
  }, [settingsPage, user]);

  // ========================================================
  //  Poll messages every 5s when a channel is active
  // ========================================================
  useEffect(() => {
    if (!activeChannel || activeTab !== "messages" || !user) return;
    const poll = setInterval(async () => {
      try {
        const data = await api<{ messages: ChatMessage[] }>(
          `/messages/${activeChannel.channelType}/${activeChannel.channelId}`
        );
        setMessagesList(data.messages.map(m => ({ ...m, isMe: m.senderId === user.id })));
      } catch {}
    }, 5000);
    return () => clearInterval(poll);
  }, [activeChannel, activeTab, user]);

  // ========================================================
  //  Auth handlers
  // ========================================================
  const handleLogin = async (email: string, password: string) => {
    const data = await api<{ accessToken: string; refreshToken: string; userId: string }>("/auth/login", {
      method: "POST", body: JSON.stringify({ email, password }),
    });
    setToken(data.accessToken);
    localStorage.setItem("ehille_refresh", data.refreshToken);
    const me = await api<{ user: User }>("/auth/me");
    setUser(me.user);
    if (me.user.groups.length > 0) {
      setActiveGroupId(me.user.groups[0].groupId);
      setScreen("main");
    } else {
      setScreen("group-select");
    }
  };

  const handleRegister = async (name: string, email: string, password: string) => {
    const data = await api<{ accessToken: string; refreshToken: string; userId: string }>("/auth/register", {
      method: "POST", body: JSON.stringify({ name, email, password }),
    });
    setToken(data.accessToken);
    localStorage.setItem("ehille_refresh", data.refreshToken);
    const me = await api<{ user: User }>("/auth/me");
    setUser(me.user);
    setScreen("group-select");
  };

  const handleGoogleLogin = async (idToken: string) => {
    const data = await api<{ accessToken: string; refreshToken: string; userId: string }>("/auth/google", {
      method: "POST", body: JSON.stringify({ idToken }),
    });
    setToken(data.accessToken);
    localStorage.setItem("ehille_refresh", data.refreshToken);
    const me = await api<{ user: User }>("/auth/me");
    setUser(me.user);
    if (me.user.groups.length > 0) {
      setActiveGroupId(me.user.groups[0].groupId);
      setScreen("main");
    } else {
      setScreen("group-select");
    }
  };

  // ========================================================
  //  Group handlers (multi-halka)
  // ========================================================
  const handleJoinGroup = async (groupId: string, password: string) => {
    await api("/groups/" + groupId + "/join", {
      method: "POST", body: JSON.stringify({ password }),
    });
    const me = await api<{ user: User }>("/auth/me");
    setUser(me.user);
    if (!activeGroupId && me.user.groups.length > 0) {
      setActiveGroupId(me.user.groups[0].groupId);
    }
    setScreen("main");
    // Refresh available groups
    api<{ groups: AvailableGroup[] }>("/groups").then(d => setAvailableGroups(d.groups)).catch(() => {});
  };

  const handleCreateGroup = async (name: string, password: string, scoreboardVisible?: boolean) => {
    await api("/groups", {
      method: "POST",
      body: JSON.stringify({ name, password, scoreboardVisible: scoreboardVisible ?? true }),
    });
    const me = await api<{ user: User }>("/auth/me");
    setUser(me.user);
    // Set newly created group as active (it will be the last one)
    const newGroup = me.user.groups[me.user.groups.length - 1];
    if (newGroup) setActiveGroupId(newGroup.groupId);
    setScreen("main");
    api<{ groups: AvailableGroup[] }>("/groups").then(d => setAvailableGroups(d.groups)).catch(() => {});
  };

  const handleLeaveGroup = async (groupId: string) => {
    await api("/groups/" + groupId + "/leave", { method: "POST" });
    const me = await api<{ user: User }>("/auth/me");
    setUser(me.user);
    // If we left the active group, switch to first remaining or null
    if (activeGroupId === groupId) {
      setActiveGroupId(me.user.groups[0]?.groupId ?? null);
    }
    api<{ groups: AvailableGroup[] }>("/groups").then(d => setAvailableGroups(d.groups)).catch(() => {});
  };

  const handleSelectGroup = (groupId: string) => {
    setActiveGroupId(groupId);
  };

  const handleOpenHatim = (groupId: string) => {
    setHatimGroupId(groupId);
    setShowHatim(true);
  };

  // ========================================================
  //  Goal handlers
  // ========================================================
  const handleToggleGoal = useCallback(async (goalId: string, completed: boolean) => {
    setGoals(prev => prev.map(g => g.id === goalId ? { ...g, completed } : g));
    try {
      const endpoint = completed ? `/goals/${goalId}/complete` : `/goals/${goalId}/uncomplete`;
      await api(endpoint, { method: "POST" });
      const me = await api<{ user: User }>("/auth/me");
      setUser(me.user);
    } catch {
      setGoals(prev => prev.map(g => g.id === goalId ? { ...g, completed: !completed } : g));
    }
  }, []);

  const handleQuizSubmit = async (answers: number[]) => {
    if (!quiz) throw new Error("No quiz");
    const data = await api<{
      score: number;
      results: { correct: boolean; points: number }[];
      alreadyAttempted?: boolean;
    }>(
      `/quiz/${quiz.id}/submit`, { method: "POST", body: JSON.stringify({ answers }) },
    );
    if (!data.alreadyAttempted) {
      const me = await api<{ user: User }>("/auth/me");
      setUser(me.user);
    }
    return data;
  };

  // ========================================================
  //  Messages handlers
  // ========================================================
  const handleSelectChannel = async (channelType: string, channelId: string) => {
    setActiveChannel({ channelType, channelId });
    try {
      const data = await api<{ messages: ChatMessage[] }>(`/messages/${channelType}/${channelId}`);
      setMessagesList(data.messages.map(m => ({ ...m, isMe: m.senderId === user?.id })));
    } catch {
      setMessagesList([]);
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!activeChannel) return;
    await api(`/messages/${activeChannel.channelType}/${activeChannel.channelId}`, {
      method: "POST", body: JSON.stringify({ text }),
    });
    const data = await api<{ messages: ChatMessage[] }>(`/messages/${activeChannel.channelType}/${activeChannel.channelId}`);
    setMessagesList(data.messages.map(m => ({ ...m, isMe: m.senderId === user?.id })));
  };

  // ========================================================
  //  Friends handlers
  // ========================================================
  const handleSendFriendRequest = async (email: string) => {
    await api("/friends/request", {
      method: "POST", body: JSON.stringify({ email }),
    });
    // Refresh friends
    const data = await api<{ friends: Friend[] }>("/friends");
    setFriends(data.friends);
  };

  const handleAcceptFriend = async (requestId: string) => {
    await api(`/friends/${requestId}/accept`, { method: "POST" });
    // Refresh both lists
    const [friendsData, reqData] = await Promise.all([
      api<{ friends: Friend[] }>("/friends"),
      api<{ requests: FriendRequest[] }>("/friends/requests"),
    ]);
    setFriends(friendsData.friends);
    setFriendRequests(reqData.requests);
  };

  const handleDeclineFriend = async (friendshipId: string) => {
    await api(`/friends/${friendshipId}/decline`, { method: "POST" });
    const data = await api<{ requests: FriendRequest[] }>("/friends/requests");
    setFriendRequests(data.requests);
  };

  const handleRemoveFriend = async (friendshipId: string) => {
    await api(`/friends/${friendshipId}`, { method: "DELETE" });
    const data = await api<{ friends: Friend[] }>("/friends");
    setFriends(data.friends);
  };

  // ========================================================
  //  Settings / Logout / Sadaka
  // ========================================================
  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem("ehille_refresh");
    setUser(null);
    setScreen("auth");
    setActiveTab("home");
    setActiveGroupId(null);
  };

  const handlePaySadaka = async () => {
    if (!user) return;
    await api("/goals/sadaka/pay", {
      method: "POST", body: JSON.stringify({ amount: user.sadakaDebt }),
    });
    const me = await api<{ user: User }>("/auth/me");
    setUser(me.user);
  };

  // ========================================================
  //  RENDER
  // ========================================================

  // -- Loading --

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-muted-foreground text-sm">Yukleniyor...</p>
        </div>
      </div>
    );
  }

  // -- Auth --
  if (screen === "auth") {
    return (
      <div className="max-w-lg mx-auto">
        <AuthScreen onLogin={handleLogin} onRegister={handleRegister} onGoogleLogin={handleGoogleLogin} />
      </div>
    );
  }

  // -- Group select --
  if (screen === "group-select") {
    return (
      <div className="max-w-lg mx-auto">
        <GroupSelectScreen
          groups={availableGroups}
          onJoin={handleJoinGroup}
          onCreate={(name, pw) => handleCreateGroup(name, pw)}
          onSkip={() => setScreen("main")}
        />
      </div>
    );
  }

  // -- Main --
  return (
    <div className="max-w-lg mx-auto min-h-screen bg-background">
      {/* Quiz modal */}
      {showQuiz && quiz && (
        <QuizModal questions={quiz.questions} onSubmit={handleQuizSubmit} onClose={() => setShowQuiz(false)} />
      )}

      {/* Hatim overlay */}
      {showHatim && hatimGroupId && (
        <HatimScreen
          groupId={hatimGroupId}
          groupName={user?.groups.find(g => g.groupId === hatimGroupId)?.groupName ?? "Halka"}
          juzList={[]}
          onAssign={async (juzNumber) => {
            await api(`/hatim/groups/${hatimGroupId}/hatim/assign`, { method: "POST", body: JSON.stringify({ juzNumber }) });
          }}
          onComplete={async (assignmentId) => {
            await api(`/hatim/groups/${hatimGroupId}/hatim/${assignmentId}/complete`, { method: "POST" });
          }}
          onBack={() => setShowHatim(false)}
          userId={user?.id ?? ""}
        />
      )}

      {/* ── Home tab ── */}
      {activeTab === "home" && (
        <HomeScreen
          userName={user?.name ?? ""}
          streak={user?.streak ?? 0}
          sadakaDebt={user?.sadakaDebt ?? 0}
          totalPoints={user?.totalPoints ?? 0}
          goals={goals}
          quote={quote}
          onToggleGoal={handleToggleGoal}
          onOpenQuiz={() => quiz && setShowQuiz(true)}
          onAddGoal={async (goal) => {
            await api("/goals", { method: "POST", body: JSON.stringify(goal) });
            const data = await api<{ goals: Goal[] }>("/goals");
            setGoals(data.goals);
          }}
          onDeleteGoal={async (goalId) => {
            try {
              await api(`/goals/${goalId}`, { method: "DELETE" });
              const data = await api<{ goals: Goal[] }>("/goals");
              setGoals(data.goals);
              const me = await api<{ user: User }>("/auth/me");
              setUser(me.user);
            } catch (e) {
              alert(e instanceof Error ? e.message : "Hedef silinemedi");
            }
          }}
        />
      )}

      {/* ── Halka tab ── */}
      {activeTab === "halka" && user && (
        <HalkaScreen
          myGroups={user.groups.map(g => ({
            groupId: g.groupId,
            groupName: g.groupName,
            role: g.role,
            memberCount: availableGroups.find(ag => ag.id === g.groupId)?.memberCount,
          }))}
          availableGroups={availableGroups.filter(
            ag => !user.groups.some(ug => ug.groupId === ag.id),
          )}
          userId={user.id}
          onJoinGroup={handleJoinGroup}
          onCreateGroup={handleCreateGroup}
          onLeaveGroup={handleLeaveGroup}
          onSelectGroup={handleSelectGroup}
          onOpenHatim={handleOpenHatim}
        />
      )}

      {/* ── Tally tab ── */}
      {activeTab === "tally" && (
        <TallyScreen
          weeklyData={weeklyData} monthlyData={monthlyData} streak={user?.streak ?? 0}
          goalTallies={goalTallies.length > 0 ? goalTallies : undefined}
        />
      )}

      {/* ── Messages tab ── */}
      {activeTab === "messages" && user && (
        <MessagesScreen
          channels={messagesChannels}
          messages={messagesList}
          activeChannel={activeChannel}
          onSelectChannel={handleSelectChannel}
          onSendMessage={handleSendMessage}
          onBack={() => setActiveChannel(null)}
        />
      )}

      {/* ── Scoreboard tab ── */}
      {activeTab === "scoreboard" && (
        <ScoreboardScreen scoreboard={scoreboard} period={scoreboardPeriod} onPeriodChange={setScoreboardPeriod} />
      )}

      {/* ── Settings tab ── */}
      {activeTab === "settings" && user && (
        settingsPage === "main" ? (
          <SettingsScreen user={user} onLogout={handleLogout} onPaySadaka={handlePaySadaka} onNavigate={setSettingsPage}
            onUpdateName={async (name) => {
              await api("/auth/profile", { method: "PATCH", body: JSON.stringify({ name }) });
              const me = await api<{ user: User }>("/auth/me");
              setUser(me.user);
            }}
          />
        ) : settingsPage === "password" ? (
          <ChangePasswordPage onBack={() => setSettingsPage("main")} onSave={async (current, newPass) => {
            await api("/auth/change-password", {
              method: "POST", body: JSON.stringify({ currentPassword: current, newPassword: newPass }),
            });
          }} />
        ) : settingsPage === "appearance" ? (
          <AppearancePage onBack={() => setSettingsPage("main")} />
        ) : settingsPage === "notifications" ? (
          <NotificationSettingsPage onBack={() => setSettingsPage("main")} />
        ) : settingsPage === "language" ? (
          <LanguagePage onBack={() => setSettingsPage("main")} onLocaleChange={(_l: Locale) => setLocaleState(v => v + 1)} />
        ) : settingsPage === "terms" ? (
          <TermsPage onBack={() => setSettingsPage("main")} />
        ) : settingsPage === "privacy" ? (
          <PrivacyPage onBack={() => setSettingsPage("main")} />
        ) : settingsPage === "help" ? (
          <HelpPage onBack={() => setSettingsPage("main")} />
        ) : settingsPage === "feedback" ? (
          <FeedbackPage onBack={() => setSettingsPage("main")} onSubmit={async (category, text) => {
            const res = await api<{ issueUrl?: string }>("/feedback", {
              method: "POST", body: JSON.stringify({ category, text }),
            });
            return res;
          }} />
        ) : settingsPage === "friends" ? (
          <FriendsScreen
            friends={friends.map(f => ({
              id: f.id, friendshipId: f.id, name: f.name, email: f.email,
              totalPoints: 0, streak: 0,
            }))}
            requests={friendRequests.map(r => ({
              id: r.id, friendshipId: r.id, name: r.fromUserName, email: r.fromUserEmail,
            }))}
            onAccept={handleAcceptFriend}
            onDecline={handleDeclineFriend}
            onSendRequest={handleSendFriendRequest}
            onRemove={handleRemoveFriend}
            onViewGoals={() => {}}
            onBack={() => setSettingsPage("main")}
          />
        ) : settingsPage === "profile" ? (
          <ProfileEditPage
            name={user.name}
            email={user.email}
            onBack={() => setSettingsPage("main")}
            onSave={async (fields) => {
              await api("/auth/profile", { method: "PATCH", body: JSON.stringify(fields) });
              const me = await api<{ user: User }>("/auth/me");
              setUser(me.user);
            }}
          />
        ) : null
      )}

      <BottomNav active={activeTab} onNavigate={setActiveTab} />
    </div>
  );
}
